import type { IndustryPage } from "@/lib/data";

export const handyman: IndustryPage = {
  slug: "handyman",
  seoTitle:
    "Handyman Website Design UK | Fixed £1,000, Easy Booking",
  seoDescription:
    "Handyman website design for UK handymen: a list of every job you do, your rates, reviews and easy booking. Fixed £1,000, live in 7 days. Book a free call.",
  tldr:
    "ConstructionWebDesign.uk offers handyman website design across the UK: a mobile-first website that lists every job you take, your rates and the area you cover, your reviews and a simple booking form. The price is £1,000, one-off and fixed, with no subscription, or £1,399 with logo and branding. It goes live in 7 days and you own the website.",
  serviceName: "Website design for handyman services",
  pricingFromGBP: 1000,
  guides: [
    { href: "/blog/how-much-should-a-construction-website-cost", label: "How much does a construction website cost in the UK?" },
    { href: "/blog/how-to-get-more-construction-leads", label: "How to get more construction leads without paying" },
    { href: "/blog/google-business-profile-for-builders", label: "Google Business Profile for builders: setup guide" },
    { href: "/blog/how-to-price-building-work", label: "How to price building work without losing money" },
  ],
  hero: {
    eyebrow: "Handyman website design · UK",
    headline: "Website design for handyman services: every job listed, easy to book.",
    highlight: "every job listed, easy to book",
    sub: "Websites for UK handyman services with every job you take listed in plain words, published rates, a list of jobs you don't do, your insurance details, your reviews and a booking form.",
    secondaryCta: { label: "See handyman demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/extension_builder_slider_ratio.jpg",
      alt: "Tradesperson working on a small home improvement job",
      headlineTop: "Fill the diary,",
      headlineBottom: "lose the paperwork.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/constructum.jpg",
    },
  ],
  demoSlugs: ["constructum", "joinwright", "brassline"],
  demosHeading: "Handyman website demo templates.",
  demosSub:
    "The closest demo templates in the ConstructionWebDesign.uk library, covering family-run building, joinery and plumbing. Open any of them to see a layout that can be adapted to a handyman job list, rates and booking form.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a handyman business need a website?",
    intro:
      "A handyman business needs a website because it answers the three questions every customer asks: do you do this job, how much will it cost, and when can you come. When the website answers them, customers can decide before they pick up the phone.",
    points: [
      {
        title: "Say what you do, and what you don't.",
        body: "Flat-pack assembly, shelving, TV mounting, easing a sticking door, clearing gutters, fence panels, tap changes, curtain poles. Naming the jobs brings in the right enquiries, and a short list of jobs you don't take stops the calls that were never going to book.",
        icon: "Toolbox",
      },
      {
        title: "Publish your rates.",
        body: "Hourly, half-day and full-day rates, the minimum charge, the area you cover and how materials are charged. A customer who sees the price before ringing already knows whether the job suits their budget.",
        icon: "Check",
      },
      {
        title: "Let people book, not just enquire.",
        body: "A booking form lets the customer pick a morning or afternoon slot and describe the job. Handyman work is small, frequent and rarely needs a quote first, so a booking often suits it better than a general enquiry form.",
        icon: "Phone",
      },
      {
        title: "Be someone customers trust in their home.",
        body: "Public liability cover, a DBS check if you have one, a real photo of you rather than a stock image, and reviews from your own area. Customers are careful about who they let into the house, and these details help them decide.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does handyman website design include?",
    intro:
      "A handyman website from ConstructionWebDesign.uk includes a plain-English list of every job you take, published rates, a booking form, a clear list of what you don't do, and your insurance and reviews. Each part either brings in the right job or saves a phone call.",
    items: [
      {
        title: "The full job list, in plain words.",
        body: "Every job you take, grouped into indoor and outdoor work and written the way a customer would describe it: \"the bathroom door won't shut properly\", not \"joinery adjustments\". Each group gets its own page, so each one has its own chance to come up when someone searches for that job.",
      },
      {
        title: "Rates and minimum charge, published.",
        body: "Hourly, half-day and full-day rates, the minimum charge, the travel area, how materials are charged and whether you take card. The most common question a handyman gets is answered before anyone has to ask it.",
      },
      {
        title: "Online booking for small jobs.",
        body: "The customer picks a half-day window, describes the job, adds a photo and gives their postcode. The booking reaches you with everything needed to confirm the slot, without a site visit or a round of phone calls.",
      },
      {
        title: "A clear list of what you don't do.",
        body: "Gas work, notifiable electrical work, structural alterations, work at height beyond your insurance: whatever your line is, the website says so. Gas work needs Gas Safe registration and notifiable electrical work falls under Part P of the Building Regulations, so being clear protects you and keeps out the jobs that go wrong.",
      },
      {
        title: "Insurance, DBS and a real photo.",
        body: "Public liability cover with the level of cover shown, a DBS check if you work for older or vulnerable customers, and a photo of the person who actually turns up. These details help a customer decide who to let into their home.",
      },
      {
        title: "Reviews from your own area.",
        body: "Genuine reviews from customers in the postcodes you cover, from Google, Checkatrade, MyBuilder or wherever you collect them, with a link to the source so customers can check them. Only real reviews are used.",
      },
      {
        title: "Repeat-customer routes.",
        body: "A book-again link for regular customers and seasonal reminders on the page, such as gutters in autumn and fence checks after winter. Repeat work from customers who already trust you is the easiest work a handyman can win.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a handyman website cost in the UK?",
      a: "A handyman website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. Typical UK ranges are about £4,000–£5,000 for a freelancer (billed £50–£120 an hour), £5,000–£25,000 for an agency, and about £22 a month for Wix or Squarespace, which adds up over the years.",
    },
    {
      q: "How long does it take to build a handyman website?",
      a: "A handyman website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The process is a discovery call, strategy and sitemap, design, build, then launch and support. Your job list, rates, service area and booking details are planned at the strategy and sitemap stage, which you sign off before design starts.",
    },
    {
      q: "What should a handyman website include?",
      a: "A handyman website should include a plain list of every job you take, a short list of jobs you don't, your hourly and half-day rates, the area you cover, a booking form, your public liability cover, a DBS check where relevant, and genuine reviews from local customers. Every handyman website from ConstructionWebDesign.uk is designed mobile-first.",
    },
    {
      q: "Does a handyman need a website if work comes from Facebook and word of mouth?",
      a: "Yes, a handyman still needs a website when work comes from Facebook and word of mouth, because both often end with someone searching your name. A Facebook page buries your rates, job list and service area under your latest posts. A website for handyman work keeps that information in one place you own, where Google can find it.",
    },
    {
      q: "Should a handyman publish prices on their website?",
      a: "Yes, a handyman should usually publish prices, because handyman jobs are small and customers decide quickly. Showing an hourly rate, a half-day rate and a minimum charge answers the most common question before anyone rings. Published prices also put off customers who were only ever going to haggle, so the calls a handyman does get are more likely to book.",
    },
    {
      q: "Can customers book a handyman online without ringing?",
      a: "Yes, a handyman website from ConstructionWebDesign.uk can include a booking form so customers book without ringing. The customer picks a half-day slot, describes the job, adds a photo and gives their postcode. The request reaches you with the details needed to confirm it, which saves a round of calls for small jobs.",
    },
    {
      q: "What jobs should a handyman say they don't do?",
      a: "A handyman should say they don't do any job that needs a registration or insurance they don't hold. That usually means gas work, which needs Gas Safe registration, notifiable electrical work under Part P unless you can self-certify through a scheme such as NICEIC or NAPIT, structural alterations, asbestos removal and work at height beyond your cover. Publishing that line helps protect your insurance position and stops the wrong calls.",
    },
    {
      q: "How can a handyman get found on Google?",
      a: "A handyman gets found on Google through a website with a page for each group of jobs, clear mention of the towns covered, a complete Google Business Profile and genuine local reviews. Every handyman website from ConstructionWebDesign.uk includes on-page local SEO, so page titles, headings and copy match how local customers search, such as \"handyman near me\" or \"flat-pack assembly\" plus your town. No one can guarantee a ranking, but these are the foundations.",
    },
  ],
  finalCta: {
    headline: "Get a website for your handyman business.",
    highlight: "your handyman business",
    sub: "Book a free 30-minute call to talk through the jobs you take and the rates you charge. The website costs a fixed £1,000 and goes live in 7 days.",
  },
};
